import { useCallback, useEffect } from 'react'
import {
  useProductStore,
  useProductStoreActions,
} from '@/modules/product/stores/product.store'
import { useAxiosHttpClient } from '@/modules/shared/infra/axios-http-client.infra'
import { HttpStatusCode } from '@/modules/shared/protocols/http-client.protocol'
import { useOrderFormStore, useOrderFormStoreActions } from '../stores/order-form.store'
import { orderFormCalculationService, type TOrderFormCalculationService } from '../services/order-form-calculation.service'
import useOrderFormParams from './use-order-form-params.hook'
import { useMainStore } from '../../shared/stores/main.store'
import type { IOrderFormJson } from '../types/order-form-json.type'

export const useOrderFormCalculation = () => {
  const { products } = useProductStore()
  const { setProducts, setProductsInsertedInOrderToCalculation } = useProductStoreActions
  const { setIsCalculationOrder, setJsonOrderForm } = useOrderFormStoreActions()
  const { jsonOrderForm, isCalculationComissionOrder, actions: { setIsCalculationComissionOrder } } = useOrderFormStore()
  const { productsInsertedInOrderToCalculation } = useProductStore()
  const { autoCalculateEnabled } = useOrderFormParams()
  const { actions: { setIsLoading, setLoadingMessage, setProgress, resetProgress } } = useMainStore()

  const { axiosRequest } = useAxiosHttpClient({
    sendRequest: orderFormCalculationService,
  })

  const updateProducts = useCallback((responseParsed: IOrderFormJson) => {
    const updatedProducts = products?.map((product) => {
      const calculatedItem = responseParsed?.itemsCalculo?.find(
        (item) => item.fkProduto === product.fkProduto,
      )
      return calculatedItem || product
    })
    setProducts(updatedProducts)
  }, [products, setProducts])

  const processCalculationResponse = useCallback((responseParsed: IOrderFormJson) => {
    const factor = responseParsed?.tipoFaturamento?.fator;
    if (factor === 0) {
      for (let i = 1; i <= 12; i++) {
        (responseParsed as any)[`condPagto${i}`] = '';
      }
    } else if (factor === 100) {
      for (let i = 13; i <= 24; i++) {
        (responseParsed as any)[`condPagto${i}`] = '';
      }
    }

    setJsonOrderForm(responseParsed)
    const productsToCalculate = responseParsed.itemsCalculo.filter(x => x.produto.idProduto === 0)

    if (productsToCalculate.length > 0) {
      const totalProducts = responseParsed.itemsCalculo.length
      const calculatedProducts = totalProducts - productsToCalculate.length

      setIsLoading(true)
      setLoadingMessage("Calculando pedido, aguarde...")

      const currentProgress = calculatedProducts === 0 ? 0 : calculatedProducts
      setProgress(currentProgress, totalProducts)

      setTimeout(() => {
        submitCalculation({
          payload: { jsonOrderForm: responseParsed }
        })
      }, 100)
    } else {
      setIsLoading(false)
      resetProgress()
    }

    updateProducts(responseParsed)

    setProductsInsertedInOrderToCalculation([])
  }, [setJsonOrderForm, setIsLoading, setLoadingMessage, setProgress, resetProgress, updateProducts, setProductsInsertedInOrderToCalculation])

  const submitCalculation = useCallback(async ({ payload }: TOrderFormCalculationService) => {
    try {
      return await axiosRequest.mutateAsync({
        showInfo: true,
        payload,
      })
    } catch (error) {
      console.error('Erro no cálculo:', error)
      setIsLoading(false)
      resetProgress()
    }
  }, [axiosRequest, setIsLoading, resetProgress])

  const calculateOrderForm = useCallback(async (jsonOfOrder?: IOrderFormJson) => {
    const payload = {
      jsonOrderForm: jsonOfOrder || jsonOrderForm,
      productIdToCalculate:
        productsInsertedInOrderToCalculation?.length > 0
          ? productsInsertedInOrderToCalculation
          : undefined,
      calculationCommissionChanged: isCalculationComissionOrder,
    }

    await submitCalculation({ payload })
    setIsCalculationComissionOrder(false)
  }, [jsonOrderForm, productsInsertedInOrderToCalculation, isCalculationComissionOrder, submitCalculation, setIsCalculationComissionOrder])

  const calculateCommission = useCallback(() => {
    const payload = {
      jsonOrderForm,
      calculationCommissionChanged: true,
    }
    submitCalculation({ payload })
  }, [jsonOrderForm, submitCalculation])

  useEffect(() => {
    if (axiosRequest.isSuccess && axiosRequest.data) {
      if (axiosRequest.data.status === HttpStatusCode.OK) {
        try {
          const responseParsed: IOrderFormJson = JSON.parse(axiosRequest.data.body)
          processCalculationResponse(responseParsed)
        } catch (error) {
          console.error('Erro ao parsear resposta:', error)
        }
      }

      setIsCalculationOrder(false)
      setProductsInsertedInOrderToCalculation([])
    }
  }, [axiosRequest.isSuccess, axiosRequest.data])

  useEffect(() => {
    if (
      axiosRequest.isPending &&
      !autoCalculateEnabled &&
      jsonOrderForm?.itemsCalculo?.some(x => x.produto.idProduto === 0)
    ) {
      setIsLoading(true)
    }
  }, [axiosRequest.isPending, autoCalculateEnabled])

  useEffect(() => {
    if (axiosRequest.isError) {
      setIsLoading(false)
      resetProgress()
      setIsCalculationOrder(false)
      console.error('Erro na requisição:', axiosRequest.error)
    }
  }, [axiosRequest.isError, axiosRequest.error])

  return {
    isCalculatingOrder: axiosRequest.isLoading,
    calculateOrderForm,
    calculateCommission,
  }
}